import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Pagination,
  InputAdornment,
  Alert,
  Snackbar,
  Stack,
  Fade,
  Grow,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  Search,
  Visibility,
  PersonAdd,
  PeopleOutlined,
  TrendingUpOutlined,
  NewReleasesOutlined,
  SupervisorAccountOutlined,
  FilterList,
  Download,
  MoreVert,
} from "@mui/icons-material";
import {
  useGetAllUsersQuery,
  useGetAdminStatsQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useCreateUserMutation,
} from "state/api";

const UserManagement = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // API hooks
  const {
    data: usersData,
    isLoading,
    refetch,
  } = useGetAllUsersQuery({
    page,
    search,
    limit: 10,
  });
  const { data: statsData } = useGetAdminStatsQuery();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [createUser] = useCreateUserMutation();

  // Form state
  const [formData, setFormData] = useState({
    pseudo: "",
    email: "",
    userName: "",
    userLastName: "",
    role: "user",
    password: "",
  });

  const handleOpenDialog = (mode, user = null) => {
    setDialogMode(mode);
    if (user) {
      setSelectedUser(user);
      setFormData({
        pseudo: user.pseudo || "",
        email: user.email || "",
        userName: user.userName || "",
        userLastName: user.userLastName || "",
        role: user.role || "user",
        password: "",
      });
    } else {
      setFormData({
        pseudo: "",
        email: "",
        userName: "",
        userLastName: "",
        role: "user",
        password: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      pseudo: "",
      email: "",
      userName: "",
      userLastName: "",
      role: "user",
      password: "",
    });
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === "create") {
        await createUser(formData).unwrap();
        setSnackbar({
          open: true,
          message: "Utilisateur créé avec succès",
          severity: "success",
        });
      } else if (dialogMode === "edit") {
        const updates = { ...formData };
        if (!updates.password) delete updates.password;
        await updateUser({ userId: selectedUser._id, updates }).unwrap();
        setSnackbar({
          open: true,
          message: "Utilisateur mis à jour avec succès",
          severity: "success",
        });
      }
      handleCloseDialog();
      // refetch() and refetchStats() are handled automatically by RTK Query cache invalidation
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Erreur: ${error.data?.message || "Une erreur est survenue"}`,
        severity: "error",
      });
    }
  };

  const handleDelete = async (userId) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")
    ) {
      try {
        await deleteUser(userId).unwrap();
        setSnackbar({
          open: true,
          message: "Utilisateur supprimé avec succès",
          severity: "success",
        });
        // refetch() and refetchStats() are handled automatically by RTK Query cache invalidation
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Erreur: ${
            error.data?.message || "Une erreur est survenue"
          }`,
          severity: "error",
        });
      }
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "error";
      case "moderator":
        return "warning";
      case "volunteer":
        return "info";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "moderator":
        return "Modérateur";
      case "volunteer":
        return "Bénévole";
      default:
        return "Utilisateur";
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        p: 3,
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          p: 3,
        }}
      >
        {/* Header */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "600",
            fontSize: "24px",
            color: "#1f2937",
            marginBottom: "24px",
            pb: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          Gestion des Utilisateurs
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                backgroundColor: "#f0f9ff",
                border: "1px solid #e0e7ff",
                borderRadius: "8px",
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}
              >
                <PeopleOutlined sx={{ fontSize: 32, color: "#3b82f6" }} />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ color: "#1e40af", fontWeight: "600" }}
                  >
                    {statsData?.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Utilisateurs
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #dcfce7",
                borderRadius: "8px",
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}
              >
                <TrendingUpOutlined sx={{ fontSize: 32, color: "#22c55e" }} />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ color: "#15803d", fontWeight: "600" }}
                  >
                    {statsData?.volunteers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bénévoles
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                backgroundColor: "#fffbeb",
                border: "1px solid #fef3c7",
                borderRadius: "8px",
              }}
            >
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}
              >
                <NewReleasesOutlined sx={{ fontSize: 32, color: "#f59e0b" }} />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{ color: "#d97706", fontWeight: "600" }}
                  >
                    {statsData?.newUsersWeek || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nouveaux (7j)
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Actions Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            placeholder="Rechercher des utilisateurs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": { borderColor: "#d1d5db" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => handleOpenDialog("create")}
            sx={{
              backgroundColor: "#3b82f6",
              color: "white",
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontWeight: "500",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Ajouter un utilisateur
          </Button>
        </Box>

        {/* Users Table */}
        <Card
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>
                    Utilisateur
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>
                    Rôle
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>
                    Date d'inscription
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#374151" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <Typography color="text.secondary">
                        Chargement...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : usersData?.users?.length > 0 ? (
                  usersData.users.map((user) => (
                    <TableRow
                      key={user._id}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#f9fafb" },
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            src={user.img}
                            alt={user.pseudo}
                            sx={{ width: 40, height: 40 }}
                          >
                            {user.pseudo?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="500">
                              {user.pseudo}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.userName} {user.userLastName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2">{user.email}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={getRoleLabel(user.role)}
                          color={getRoleColor(user.role)}
                          size="small"
                          sx={{ borderRadius: "6px" }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(user.accountCreatedAt).toLocaleDateString(
                            "fr-FR"
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog("view", user)}
                            sx={{
                              color: "#6b7280",
                              "&:hover": {
                                backgroundColor: "#f3f4f6",
                                color: "#3b82f6",
                              },
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog("edit", user)}
                            sx={{
                              color: "#6b7280",
                              "&:hover": {
                                backgroundColor: "#f3f4f6",
                                color: "#3b82f6",
                              },
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(user._id)}
                            sx={{
                              color: "#6b7280",
                              "&:hover": {
                                backgroundColor: "#fef2f2",
                                color: "#ef4444",
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                      <Typography color="text.secondary">
                        Aucun utilisateur trouvé
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {usersData?.totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 3,
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <Pagination
                count={usersData.totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "6px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#3b82f6",
                    "&:hover": {
                      backgroundColor: "#2563eb",
                    },
                  },
                }}
              />
            </Box>
          )}
        </Card>

        {/* User Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "600", color: "#1f2937" }}
            >
              {dialogMode === "create" && "Créer un nouvel utilisateur"}
              {dialogMode === "edit" && "Modifier l'utilisateur"}
              {dialogMode === "view" && "Détails de l'utilisateur"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Pseudo"
                  value={formData.pseudo}
                  onChange={(e) =>
                    setFormData({ ...formData, pseudo: e.target.value })
                  }
                  fullWidth
                  disabled={dialogMode === "view"}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#d1d5db" },
                      "&:hover fieldset": { borderColor: "#3b82f6" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  fullWidth
                  disabled={dialogMode === "view"}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#d1d5db" },
                      "&:hover fieldset": { borderColor: "#3b82f6" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Prénom"
                  value={formData.userName}
                  onChange={(e) =>
                    setFormData({ ...formData, userName: e.target.value })
                  }
                  fullWidth
                  disabled={dialogMode === "view"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#d1d5db" },
                      "&:hover fieldset": { borderColor: "#3b82f6" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nom"
                  value={formData.userLastName}
                  onChange={(e) =>
                    setFormData({ ...formData, userLastName: e.target.value })
                  }
                  fullWidth
                  disabled={dialogMode === "view"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#d1d5db" },
                      "&:hover fieldset": { borderColor: "#3b82f6" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  disabled={dialogMode === "view"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#d1d5db" },
                      "&:hover fieldset": { borderColor: "#3b82f6" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                    },
                  }}
                >
                  <InputLabel>Rôle</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    label="Rôle"
                  >
                    <MenuItem value="user">Utilisateur</MenuItem>
                    <MenuItem value="volunteer">Bénévole</MenuItem>
                    <MenuItem value="moderator">Modérateur</MenuItem>
                    <MenuItem value="admin">Administrateur</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {(dialogMode === "create" || dialogMode === "edit") && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={
                      dialogMode === "create"
                        ? "Mot de passe"
                        : "Nouveau mot de passe (optionnel)"
                    }
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    fullWidth
                    required={dialogMode === "create"}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": { borderColor: "#d1d5db" },
                        "&:hover fieldset": { borderColor: "#3b82f6" },
                        "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                      },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleCloseDialog}
              sx={{
                borderRadius: "8px",
                color: "#6b7280",
                "&:hover": { backgroundColor: "#f3f4f6" },
              }}
            >
              Annuler
            </Button>
            {dialogMode !== "view" && (
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  backgroundColor: "#3b82f6",
                  borderRadius: "8px",
                  px: 3,
                  fontWeight: "500",
                  "&:hover": { backgroundColor: "#2563eb" },
                }}
              >
                {dialogMode === "create" ? "Créer" : "Mettre à jour"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default UserManagement;
